import React, { useState, memo, useEffect } from "react";
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import { getIn } from "formik";
import { Control, Option, IndicatorsContainer } from "./SelectAutocompleteComponents";

import { ErrorMessage, ContexualHelp } from "../../../Notifications";
import { FieldDescription } from "../../../FieldDescription/FieldDescription";
import { checkBrowserIsIE } from "../../../../utils/checkBrowserIsIE";
import { areEqualFieldProps } from "../../utils";
import { FormControlRoot, customStyles } from "./styled";

const components = {
  Control,
  Option,
  IndicatorsContainer
};

const AsyncSelectAutoCompleteBase = ({
  extractValue = option => option.value,
  extractLabel = option => option?.label || option?.displayText,
  theme,
  label,
  placeholder,
  shrink,
  field: { onBlur, ...field },
  form: { errors, touched, setFieldValue, setFieldTouched },
  multiple = false,
  disabled = false,
  contextualHelpText,
  contextualHelpProps = {},
  placement,
  onChange = value => setFieldValue(field.name, value),
  getDatalistSearch,
  codeType,
  defaultValue,
  infoTitle,
  showInLineError = false,
  classes: extendedClasses = "",
  warning = false,
  warningText = "",
  ErrorMessageComponent = ErrorMessage,
  showCounter = false,
  "data-testid": testId = "select-autocomplete-field",
  ...props
}) => {
  const errorMessage = getIn(errors, field.name);
  const isError = errorMessage && getIn(touched, field.name);
  const [hasFocus, setFocus] = useState(false);
  const [hasClick, setClick] = useState(false);
  const isIE = checkBrowserIsIE();
  const [value, setValue] = useState();
  const [inputValue, setInputValue] = useState("");

  // Initialize with defaultValue or field.value if available
  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
      setInputValue(defaultValue.displayText);
    }
  }, [defaultValue]);
  /**
   * Handles the change event when a user selects an option
   * @param {Object} selectedValue - The selected option object
   * @returns {void}
   */
  const handleChange = selectedValue => {
    // Update the local state with the full selected option object
    setValue(selectedValue);

    // Update input value when an option is selected
    if (selectedValue) {
      setInputValue(extractLabel(selectedValue));
    } else {
      setInputValue("");
    }

    // Pass the extracted value to onChange (typically setFieldValue)
    const extractedValue = selectedValue ? extractValue(selectedValue) : "";
    return onChange(extractedValue);
  };

  /**
   * Handles changes to the input value as the user types
   * @param {string} value - The current input value
   * @returns {void}
   */
  const handleInputChange = value => {
    if (value) {
      setInputValue(value);
    }
  };

  const loadOptions = async (inputValue, callback) => {
    try {
      let searchPattern = inputValue || "";
      let searchData = { codeType, searchPattern };

      // Only search if we have at least 2 characters
      if (searchPattern && searchPattern.length >= 2) {
        const response = await getDatalistSearch(searchData);

        // Safety check for response
        if (response && typeof response === "object") {
          const json = Object.values(response)[0] || [];

          // Make sure json is an array before mapping
          if (Array.isArray(json)) {
            const options = json.map(i => ({
              code: i?.code ? `${i.code}` : "",
              value: i?.value ? `${i.value}` : "",
              displayText: i?.displayText ? `${i.displayText}` : "",
              key: i?.key ? `${i.key}` : ""
            }));
            return callback(options);
          }
        }
      }

      // Return empty array if no search results or search pattern is too short
      return callback([]);
    } catch (error) {
      console.error("Error loading options:", error);
      // Always return an empty array to prevent "in operator" errors
      return callback([]);
    }
  };

  const renderValue = inputValue || defaultValue;
  // Safely create default options array, ensuring we don't push null/undefined values
  const defaultLoadOptionsArr = defaultValue ? [defaultValue] : [];

  const handleFocus = () => {
    setFocus(true);
  };

  const handleBlur = event => {
    // Prevent "Cannot read properties of undefined (reading 'persist')" error
    if (event && typeof event.persist === "function") {
      event.persist();
    }

    setFocus(false);
    setClick(false);
    setFieldTouched(field.name);
    if (onBlur) {
      // Pass the event to onBlur if it exists
      onBlur(event);
    }
  };
  // Define color constants for better maintainability
  const COLORS = {
    ERROR: "#ea2b1e",
    WARNING: "#ea991d",
    FOCUSED: "var(--Color-Text-Primary-On-Light, #1F1F1F)",
    DEFAULT: "#BCBDC0"
  };

  // Adjust customStyles based on error and warning states
  const adjustedStyles = {
    ...customStyles,
    control: (base, state) => {
      // Get border color based on field state
      const getBorderColor = () => {
        if (isError) return COLORS.ERROR;
        if (warning) return COLORS.WARNING;
        if (state.isFocused) return COLORS.FOCUSED;
        return COLORS.DEFAULT;
      };

      return {
        ...customStyles.control(base, state),
        borderColor: getBorderColor()
      };
    }
  };

  return (
    <FormControlRoot className={`formControl ${extendedClasses}`} data-testid={`${testId}Parent`}>
      <ContexualHelp title={contextualHelpText} {...contextualHelpProps} placement={placement}>
        <AsyncSelect
          {...field}
          {...props}
          isClearable
          isDisabled={disabled}
          defaultOptions={defaultLoadOptionsArr}
          placeholder={hasClick && placeholder}
          loadOptions={loadOptions}
          onInputChange={handleInputChange}
          maxMenuHeight={250}
          cacheOptions={true}
          styles={adjustedStyles}
          components={components}
          value={value || defaultValue}
          onChange={handleChange}
          textFieldProps={{
            onFocus: handleFocus,
            onClick: () => setClick(true),
            onBlur: handleBlur,
            label,
            error: !!isError,
            InputLabelProps: {
              shrink: shrink || hasFocus || !!renderValue
            },
            "aria-invalid": !!isError,
            "aria-describedby": isError ? `${testId}-error` : undefined,
            className: !isError && warning ? "warning" : ""
          }}
          noOptionsMessage={() => "No search results found"}
          getOptionLabel={extractLabel}
        />
      </ContexualHelp>

      {isIE && !field?.value && hasFocus && (
        <span className="iePlaceholder" aria-hidden="true">
          {placeholder}
        </span>
      )}

      {(infoTitle || props.maxLength) && (
        <FieldDescription
          title={infoTitle}
          fieldValueLength={typeof inputValue === "string" ? inputValue.length : 0}
          fieldMaxLength={props.maxLength}
          showInLineError={showInLineError}
          errorMessage={errorMessage}
          showWarning={warning}
          isError={isError}
          warningDesc={warningText}
          showCounter={showCounter}
          id={testId}
        />
      )}

      {!showInLineError && isError && (
        <ErrorMessageComponent error={errorMessage} id={`${testId}-error`} />
      )}

      {!showInLineError && warning && !isError && (
        <p className="warningContainer" role="alert">
          {warningText}
        </p>
      )}
    </FormControlRoot>
  );
};

/**
 * PropTypes for AsyncSelectAutoCompleteBase component
 */
AsyncSelectAutoCompleteBase.propTypes = {
  /**
   * Function to extract value from option object
   */
  extractValue: PropTypes.func,

  /**
   * Function to extract label from option object
   */
  extractLabel: PropTypes.func,

  /**
   * Theme object from MUI
   */
  theme: PropTypes.object,

  /**
   * Label for the select field
   */
  label: PropTypes.string,

  /**
   * Placeholder text for the select field
   */
  placeholder: PropTypes.string,

  /**
   * Whether to shrink the label
   */
  shrink: PropTypes.bool,

  /**
   * Field props from Formik
   */
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    onChange: PropTypes.func,
    onBlur: PropTypes.func
  }).isRequired,

  /**
   * Form props from Formik
   */
  form: PropTypes.shape({
    touched: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired
  }).isRequired,

  /**
   * Whether multiple selections are allowed
   */
  multiple: PropTypes.bool,

  /**
   * Whether the select is disabled
   */
  disabled: PropTypes.bool,

  /**
   * Text for contextual help popup
   */
  contextualHelpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * Additional props for contextual help component
   */
  contextualHelpProps: PropTypes.object,

  /**
   * Placement for the contextual help tooltip
   */
  placement: PropTypes.string,

  /**
   * Custom change handler
   */
  onChange: PropTypes.func,

  /**
   * Function to search for options from API
   */
  getDatalistSearch: PropTypes.func.isRequired,

  /**
   * Type of code to search for (used in API requests)
   */
  codeType: PropTypes.string.isRequired,

  /**
   * Default value for the select
   */
  defaultValue: PropTypes.shape({
    code: PropTypes.string,
    value: PropTypes.string,
    displayText: PropTypes.string,
    key: PropTypes.string
  }),

  /**
   * Informational title displayed below the field
   */
  infoTitle: PropTypes.string,

  /**
   * Whether to show error messages inline
   */
  showInLineError: PropTypes.bool,

  /**
   * Additional CSS classes for the component
   */
  classes: PropTypes.string,

  /**
   * Whether to show warning state
   */
  warning: PropTypes.bool,

  /**
   * Warning text to display
   */
  warningText: PropTypes.string,

  /**
   * Custom error message component
   */
  ErrorMessageComponent: PropTypes.elementType,

  /**
   * Whether to show character counter
   */
  showCounter: PropTypes.bool,

  /**
   * Test ID for the component
   */
  "data-testid": PropTypes.string,

  /**
   * Maximum length for the input value
   */
  maxLength: PropTypes.number
};

export const AsyncSelectAutoComplete = memo(AsyncSelectAutoCompleteBase, areEqualFieldProps);
